import Grid from '@material-ui/core/Grid'
import { isEqual } from 'lodash/fp'
import Typography from '@material-ui/core/Typography'
import { Form, Formik, getIn } from 'formik'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '../TextField'
import { amountFilter } from '../../helpers'
import theme from '../../theme'
import {
  AttachmentListRespType,
  CaseReportId,
  AttachmentType,
  PartType,
} from '../../models'
import { useStyles } from '../../pages/styled'
import { Attachments, AttachmentsChangedEvent } from '../Attachments'
import {
  combineAttachmentsList,
  getAttachmentsFromAttachmentListOrEmpty,
} from '../../models/attachmentListFnc'
import * as yup from 'yup'
import { QuestionMark } from '../QuestionMark'
import { Ga4Form } from './Ga4Form'
import ErrorSubmitBack from '../ErrorSubmitBack'
import { amountReplace, emptyStrToUndefined, stringToNumber } from '../../helpers/miscFunctions'

export type CaseCATreatmentDataFormType = {
  priceCur?: number | null
  refundCur?: number | null
  currency?: string | null
  attachmentList?: AttachmentListRespType[]
}

const CancellationExpensesForm = (params: {
  caseReportId: CaseReportId
  cancelTreatmentData: CaseCATreatmentDataFormType | undefined
  onPageContinue: (cancelData: CaseCATreatmentDataFormType) => void
  onPageBack: () => void
  isContinueInProgress: boolean
  isDataLoading?: boolean
  errorMessage: string
  setIfEqual: (isChanged: boolean) => void
}) => {
  const classes = useStyles(theme)
  const { t } = useTranslation()

  const handleFormSubmit = (values: any) => {
    const cancelTreatmentDataParams: CaseCATreatmentDataFormType =
      transformFormikValueToCancellationExpensesData(values)

    // 2. pošlu data z komponenty nahoru
    params.onPageContinue(cancelTreatmentDataParams)
  }

  function transformFormikValueToCancellationExpensesData(
    values: any
  ): CaseCATreatmentDataFormType {
    return {
      // NAPLNIT DATY Z FORMULARE
      priceCur: stringToNumber(values.priceCur),
      refundCur: stringToNumber(values.refundCur),
      // 3 písmenný ISO kód měny
      currency: emptyStrToUndefined(values.currency),
      attachmentList: combineAttachmentsList(
        params.cancelTreatmentData?.attachmentList, // original
        [
          {
            attachmentType: AttachmentType.INVOICE,
            attachments: values.attachments,
          },
        ]
      ),
    }
  }

  // Funkce 'handleBackButtonClicked' je volana z BackButton
  const handleBackButtonClicked = () => {
    params.onPageBack()
  }

  const transformCATreatmentDataToFormikValue: any = (
    treatmentCAData?: CaseCATreatmentDataFormType | undefined
  ) => {
    // INICIALIZACE FORMULARE
    return {
      priceCur: (treatmentCAData?.priceCur ?? '').toString(),
      refundCur: (treatmentCAData?.refundCur ?? '').toString(),
      currency: treatmentCAData?.currency ?? 'PLN',
      attachments: getAttachmentsFromAttachmentListOrEmpty(
        treatmentCAData?.attachmentList,
        AttachmentType.INVOICE
      ),
    }
  }

  //console.log(params.cancelTreatmentData, 'cancel-treatment-data')

  const CancellationExpensesSchema = () =>
    yup.object().shape({
      priceCur: yup
        .string()
        .required(t('cancellationPg.priceCur.required'))
        .test(
          'numberFormat',
          t('message.err'),
          (val: string | undefined) => {
            const _number = Number(val)
            return !isNaN(_number)
          }
        )
        .test(
          'moreThan0',
          t('amount.moreThan0'),
          (val: string | undefined) => {
            const _number = Number(val)
            return _number > 0
          }
        ),
      refundCur: yup
        .string()
        .required(t('cancellationPg.refundCur.required'))
        .test(
          'numberFormat',
          t('message.err'),
          (val: string | undefined) => {
            const _number = Number(val)
            return !isNaN(_number)
          }
        )
        .test(
          'moreThanRefundCur',
          t('cancellationPg.refundCur.min'),
          (val: string | undefined) => {
            const _refundCur = Number()
            if (isNaN(_refundCur)) return true
            const _number = Number(val)
            return _number >= _refundCur
          }
        )
      ,
      attachments: yup.array().nullable().min(1, t('uploadFile.err1')),
    })

  const formikRef = useRef(null)
  return (
    <Ga4Form formName={'HelthProblemForm'} >
      <Formik
        initialValues={transformCATreatmentDataToFormikValue(
          params.cancelTreatmentData
        )}
        validationSchema={CancellationExpensesSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formikRef}
      >
        {({
          initialValues,
          values,
          errors,
          setFieldValue,
          touched,
          handleBlur,
        }) => {
          const isChanged = isEqual(values, initialValues)
          params.setIfEqual(isChanged)
          return (
            <Form className={classes.root}>
              <Grid
                item
                sm={12}
                xl={12}
                xs={12}
                container
                direction="row"
                className={classes.gridMainForm}
              >
                <Grid
                  item
                  sm={9}
                  xl={9}
                  xs={12}
                  container
                  direction="row"
                  className={classes.gridWorkArea}
                >
                  <Grid
                    item
                    sm={12}
                    xs={12}
                    xl={12}
                    container
                    className={classes.gridResize}
                  >
                    <Typography className={classes.text} variant="h3">
                      {t('cancellationExpensesPg.title')}
                    </Typography>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={6}
                        xs={6}
                        sm={6}
                        className={classes.fieldSize}
                      >
                        <TextField
                          regex={amountFilter}
                          name="priceCur"
                          value={values.priceCur}
                          helperText={
                            errors.priceCur && touched.priceCur
                              ? errors.priceCur
                              : null
                          }
                          error={Boolean(errors.priceCur && touched.priceCur)}
                          label={t('cancellationPg.priceCur.label')}
                          placeholder={t('forthPg.amount.hint')}
                          onChange={(e) => {
                            setFieldValue(
                              'priceCur',
                              amountReplace(e.target.value)
                            )
                          }}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{ maxLength: 12 }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      xl={12}
                      container
                      className={classes.gridItem}
                      spacing={2}
                    >
                      <Grid
                        item
                        xl={6}
                        xs={6}
                        sm={6}
                        className={classes.fieldSize}
                      >
                        <TextField
                          regex={amountFilter}
                          name="refundCur"
                          value={values.refundCur}
                          helperText={
                            errors.refundCur && touched.refundCur
                              ? errors.refundCur
                              : null
                          }
                          error={Boolean(errors.refundCur && touched.refundCur)}
                          label={t('cancellationPg.refundCur.label')}
                          placeholder={t('forthPg.amount.hint')}
                          onChange={(e) => {
                            setFieldValue(
                              'refundCur',
                              amountReplace(e.target.value)
                            )
                          }}
                          className={classes.fieldSize}
                          InputProps={{ required: false }}
                          inputProps={{ maxLength: 12 }}
                          onBlur={handleBlur}
                          required
                        />
                      </Grid>
                    </Grid>
                    <Attachments
                      name={'attachments'}
                      caseReportId={params.caseReportId}
                      labelText={
                        <>
                          {t('uploadAttachments.CASE.INVOICE')}
                          <QuestionMark
                            text={t('cancellationExpensesPg.questionMarkLabel')}
                            height={20}
                          />
                        </>
                      }
                      attachmentType={AttachmentType.INVOICE}
                      part={PartType.CASE}
                      attachments={values.attachments}
                      onAttachmentsChanged={(event: AttachmentsChangedEvent) => {
                        setFieldValue('attachments', event.value.attachments)
                      }}
                      onBlur={handleBlur}
                      required
                      error={!!(
                        getIn(errors, 'attachments') &&
                        getIn(touched, 'attachments'))
                      }
                      helperText={
                        getIn(errors, 'attachments') &&
                          getIn(touched, 'attachments')
                          ? getIn(errors, 'attachments')
                          : null
                      }
                    />
                    <ErrorSubmitBack
                      formik={formikRef.current}
                      pending={params.isContinueInProgress}
                      backLinkText={t('forthPg.backBtn')}
                      onBackLinkClick={handleBackButtonClicked}
                    >
                      {t('continueBtn')}
                    </ErrorSubmitBack>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>
    </Ga4Form >
  )
}

export default CancellationExpensesForm
